import { createSchemaFaq } from '../seo-schemas/faq';

export type Params = {
  city: string;
  state: string;
  averagePrice?: string;
}
export const getCityServiceStructuredFaq = ({
  city,
  state,
}: Params) => {
  const date = new Date();
  const month = date.toLocaleString('default', {
    month: 'long',
  });
  const year = date.toLocaleString('default', {
    year: 'numeric',
  });

  const faqStructuredData = createSchemaFaq({
    entities: [{
      question: `What is the average cost of a dog walker in ${city}, ${state} in ${month} ${year}?`,
      answer: 'The average price for a 20 minute dog walk is $16.00 and the average price for a 30 minute walk is $22.00. This is based on the prices set by Wag! Pet Caregivers and may vary by caregiver, location, or time.',
    }, {
      question: `How do I find a dog walker in ${city} ${state}?`,
      answer: `Wag! offers multiple ways to find and match with caregivers for dog walking or short-term drop-ins in ${city} ${state}. You can view profiles of local caregivers or submit a request to the marketplace and caregivers will opt-in to your posting.`,
    }],
  });

  return faqStructuredData;
};

export type FAQ = {
  header: string;
  body: string;
};

export const getCityWalkingServiceFaq = ({
  city,
  state,
}: Params) => {
  const faq: FAQ[] = [{
    header: `How much does a dog walk cost in ${city}, ${state}?`,
    body: `The average cost of a dog walk in ${city} is $16.00. The price of a dog walk on the Wag! platform is based on the price set by the Pet Caregiver and varies depending on a variety of factors, including the number of dogs, where you live, the length of the walk, and any additional fees that may apply.`,
  }, {
    header: `How do I find and choose a dog walker in ${city}, ${state}?`,
    body: `Finding a dog walker in ${city} is simple on the Wag! app. Wag! instantly connects you with top-rated, vetted dog walkers near you. When scheduling your on-demand dog walk, you can choose “Quick Match” to allow the platform to match you with the best available Caregiver, or “Pet Parent Match” to see all available Caregivers and pick the best match for you.`,
  }, {
    header: `How long does a dog walk last in ${city}, ${state}?`,
    body: `The length of a dog walk in ${city} depends on your pup's needs. The Wag! app lets you choose between a 20-minute, 30-minute, or 60-minute stroll for your dog.`,
  }, {
    header: `How to book a dog walk in ${city}, ${state}?`,
    body: `To book a dog walk in ${city} simply open the Wag! app, choose the Walking service, then follow the instructions to select the length and schedule of the walk. You’ll need to provide home access notes and pet profile information before confirming your details to book the walk.`,
  }];

  const faqStructuredData = createSchemaFaq({
    entities: faq.map((item) => ({
      question: item.header,
      answer: item.body,
    })),
  });

  return {
    faq,
    structuredData: faqStructuredData,
  };
};

export const getCityBoardingServiceFaq = ({
  city,
  state,
}: Params) => {
  const faq = [{
    header: `How much does a dog boarding cost in ${city}, ${state}?`,
    body: `The average cost of a dog boarding or pet sitting in ${city} is $48 - $69 per night. The price of overnight pet care on the Wag! platform is based on the price set by the Pet Caregiver and varies depending on a variety of factors, including the number of dogs, where you live, the number of days/nights, and any additional fees that may apply.`,
  }, {
    header: `How do I find and choose a pet sitter in ${city}, ${state}?`,
    body: `Finding a dog boarder or pet sitter in ${city} is simple on the Wag! app. Wag! instantly connects you with top-rated, vetted Pet Caregivers near you. When scheduling overnight pet care, you can choose “Quick Match” to allow the platform to match you with the best available Caregiver, or “Pet Parent Match” to see all available Caregivers and pick the best match for you.`,
  }, {
    header: `Do pet sitters stay overnight?`,
    body: `Up to you! In ${city} and all other Wag! locations, Pet Parents can choose to book a pet sitter who watches your dog or other pets in your home or a dog boarder who hosts your pet in their home. You decide which environment would be most comfortable for your dog and your family.`,
  }, {
    header: `How to book a dog boarding or pet sitting in ${city}, ${state}?`,
    body: `To book a dog sitter or boarder in ${city} simply open the Wag! app, choose the Sitting or Boarding service, then follow the instructions to schedule your overnight care. You’ll need to provide additional information such as home access notes, pet profile and pick-up or drop-off preferences before confirming your request.`,
  }];

  const faqStructuredData = createSchemaFaq({
    entities: faq.map((item) => ({
      question: item.header,
      answer: item.body,
    })),
  });

  return {
    faq,
    structuredData: faqStructuredData,
  };
};

export const getCityTrainingServiceFaq = ({
  city,
  state,
}: Params) => {
  const faq = [{
    header: `How much does dog training cost in ${city}, ${state}?`,
    body: `The average cost of a dog training session in ${city} is $60 per hour. The price of in-home training on the Wag! platform is based on the price set by the Pet Caregiver and varies depending on a variety of factors, including the number of dogs, where you live, and any additional fees that may apply.`,
  }, {
    header: `How do I find and choose a dog trainer in ${city}, ${state}?`,
    body: `Finding a dog trainer in ${city} is simple on the Wag! app. Wag! instantly connects you with top-rated, vetted dog trainers near you. When booking an in-home training session, you can choose “Quick Match” to allow the platform to match you with the best available Caregiver, or “Pet Parent Match” to see all available Caregivers and pick the best match for you.`,
  }, {
    header: `How to book a dog trainer in  ${city}, ${state}?`,
    body: `To book a dog trainer in ${city} simply open the Wag! app, choose the Training service, then follow the instructions to schedule a 60 minute in-home training. You’ll need to provide additional details about your dog before confirming your request.`,
  }];

  const faqStructuredData = createSchemaFaq({
    entities: faq.map((item) => ({
      question: item.header,
      answer: item.body,
    })),
  });

  return {
    faq,
    structuredData: faqStructuredData,
  };
};

export const getCityGroomingServiceFaq = ({
  city,
  state,
}: Params) => {
  const faq = [{
    header: `How much does a grooming in cost in ${city}?`,
    body: `The average cost of an excursion in ${city} is $60. The price of a drop-in on the Wag! platform is based on the price set by the Pet Caregiver and varies depending on a variety of factors, including where you live, the length of the excursion, and any additional fees that may apply.`,
  }, {
    header: `How do I find a groomer in ${city}, ${state}?`,
    body: `Finding a groomer in ${city} is simple on the Wag! app. Wag! connects you with top-rated, vetted groomers near you. Browse and book your favorite caregiver and then message them within the app with any additional details.`,
  }, {
    header: `How do I book a groomer near ${city}, ${state}?`,
    body: `To book a grooming near ${city} simply browse for your favorite caregiver and select "Book" from their profile. Once you've submitted details about your request they wil have the chance to accept your request. After accepting you'll be able to message your caregiver within the app about any additional details for your grooming.`,
  }];

  const faqStructuredData = createSchemaFaq({
    entities: faq.map((item) => ({
      question: item.header,
      answer: item.body,
    })),
  });

  return {
    faq,
    structuredData: faqStructuredData,
  };
};

export const getCityDayCareServiceFaq = ({
  city,
  state,
  averagePrice = '$60',
}: Params) => {
  const faq = [{
    header: `How much does a daycare or drop-in cost in ${city}?`,
    body: `The average cost of a drop-in in ${city} is ${averagePrice}. The price of a drop-in on the Wag! platform is based on the price set by the Pet Caregiver and varies depending on a variety of factors, including where you live, the length of the drop-in, and any additional fees that may apply.`,
  }, {
    header: `How do I find and choose a drop-in caregiver in ${city}, ${state}?`,
    body: `Finding a caregiver in ${city} is simple on the Wag! app. Wag! instantly connects you with top-rated, vetted dog caregivers near you. When scheduling your drop-in, you can choose “Quick Match” to allow the platform to match you with the best available Caregiver, or “Pet Parent Match” to see all available Caregivers and pick the best match for you.`,
  }, {
    header: `What does a daycare involve in ${city}, ${state}?`,
    body: `The Wag! app lets you choose between a 20-minute, 30-minute, or 60-minute drop-in for you dog. These can involve play time, potty breaks, food and water refills, as well as custom requests you can share with your caregiver. You can select the duration in the app and then message your caregiver after booking with more details about what you'd like.`,
  }, {
    header: `How do I book a drop-in near ${city}, ${state}?`,
    body: `To book a drop-in near ${city} simply browse for your favorite caregiver and select "Book" from their profile. Once you've submitted details about your request they wil have the chance to accept your request. After accepting you'll be able to message your caregiver within the app about any additional details for your drop-in.`,
  }];

  const faqStructuredData = createSchemaFaq({
    entities: faq.map((item) => ({
      question: item.header,
      answer: item.body,
    })),
  });

  return {
    faq,
    structuredData: faqStructuredData,
  };
};

export const catBoardingServiceFaq = ({
  city,
  state,
  averagePrice = '$60',
}: Params) => {
  const faq = [{
    header: `How much do cat sittings, boardings, or drop-ins cost in ${city}?`,
    body: `The average cost of a cat sitting in ${city} is ${averagePrice}. The average price for a boarding is <boarding-price> and the average price for a drop-in is $60. The price on the Wag! platform is based on the price set by the Pet Caregiver and varies depending on a variety of factors, including where you live, the length of service, and any additional fees that may apply.`,
  }, {
    header: `How do I find a cat sitter, boarder, or daycare in ${city}, ${state}?`,
    body: `Finding a cat sitter, boarder, or daycare in ${city} is simple on the Wag! app. Wag! connects you with top-rated, vetted sitters and boarders near you. Browse and book your favorite sitter or boarder and then message them within the app with any additional details.`,
  }, {
    header: `Do cat sitters stay overnight?`,
    body: `Up to you! In ${city} and all other Wag! locations, Pet Parents can choose to book a pet sitter who watches your cat or other pets in your home or a dog boarder who hosts your pet in their home. You decide which environment would be most comfortable for your cat and your family.`,
  }, {
    header: `How do I book a sitter, boarder, or daycare near ${city}, ${state}?`,
    body: `To book a cat sitting, boarding, or drop-in near <city> simply browse for your favorite caregiver and select "Book" from their profile. Once you've submitted details about your request they wil have the chance to accept your request. After accepting you'll be able to message your caregiver within the app about any additional details about your cat's care.`,
  }];

  const faqStructuredData = createSchemaFaq({
    entities: faq.map((item) => ({
      question: item.header,
      answer: item.body,
    })),
  });

  return {
    faq,
    structuredData: faqStructuredData,
  };
};

export const getCityDogExcursionsServiceFaq = ({
  city,
  state,
  averagePrice = '$60',
}: Params) => {
  const faq = [{
    header: `How much do excursions like a hike, run, or swim cost in ${city}?`,
    body: `The average cost of an excursion in ${city} is ${averagePrice}. The price on the Wag! platform is based on the price set by the Pet Caregiver and varies depending on a variety of factors, including where you live, the length of service, and any additional fees that may apply.`,
  }, {
    header: `How do I find a caregiver for a dog hike, run, or swim in ${city}, ${state}?`,
    body: `Finding a dog procession to take your dog on an excursion near ${city} is simple on the Wag! app. Wag! connects you with top-rated, vetted caregivers near you. Browse and book your favorite caregiver and then message them within the app with any additional details.`,
  }, {
    header: `How do I book an excursion near ${city}, ${state}?`,
    body: `To book an excursion near ${city} simply browse for your favorite caregiver and select "Book" from their profile. Once you've submitted details about your request they wil have the chance to accept your request. After accepting you'll be able to message your caregiver within the app about any additional details for your excursion.`,
  }];

  const faqStructuredData = createSchemaFaq({
    entities: faq.map((item) => ({
      question: item.header,
      answer: item.body,
    })),
  });

  return {
    faq,
    structuredData: faqStructuredData,
  };
};
